// @ts-nocheck
// Cached API wrappers for skill-related endpoints
// These endpoints are called directly with fetch() and need caching

import { API_BASE } from '~/api'
import { cachedFetch, invalidateCache } from '~/utils/issues/requestCache'

// Skill Files
export async function getSkillFiles(skillHash: string): Promise<any> {
  return await cachedFetch(`${API_BASE}/skills/${skillHash}/files/`)
}

export async function getSkillFile(skillHash: string, fileHash: string): Promise<any> {
  return await cachedFetch(`${API_BASE}/skills/${skillHash}/files/${fileHash}/`)
}

// POST operations that need cache invalidation
export async function createSkillFile(skillHash: string, fileData: any): Promise<any> {
  const response = await fetch(`${API_BASE}/skills/${skillHash}/files/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fileData),
  })

  if (!response.ok)
    throw new Error(`Failed to create file: ${response.statusText}`)

  // Invalidate files cache
  invalidateCache(`skills/${skillHash}/files`)

  return response.json()
}

export async function deleteSkillFile(skillHash: string, fileHash: string): Promise<any> {
  const response = await fetch(`${API_BASE}/skills/${skillHash}/files/${fileHash}/`, {
    method: 'DELETE',
  })

  if (!response.ok)
    throw new Error(`Failed to delete file: ${response.statusText}`)

  // Invalidate files cache
  invalidateCache(`skills/${skillHash}/files`)
  invalidateCache(`skills/${skillHash}/files/${fileHash}`)

  return response.json()
}

export async function updateSkillFile(skillHash: string, fileHash: string, fileData: any): Promise<any> {
  const response = await fetch(`${API_BASE}/skills/${skillHash}/files/${fileHash}/`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fileData),
  })

  if (!response.ok)
    throw new Error(`Failed to update file: ${response.statusText}`)

  // Invalidate files cache
  invalidateCache(`skills/${skillHash}/files`)
  invalidateCache(`skills/${skillHash}/files/${fileHash}`)

  return response.json()
}

// File Version Operations
export async function addFileVersion(skillHash: string, fileHash: string, versionData: any): Promise<any> {
  const response = await fetch(`${API_BASE}/skills/${skillHash}/files/${fileHash}/versions/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(versionData),
  })

  if (!response.ok)
    throw new Error(`Failed to add file version: ${response.statusText}`)

  // Invalidate files cache
  invalidateCache(`skills/${skillHash}/files`)
  invalidateCache(`skills/${skillHash}/files/${fileHash}`)

  return response.json()
}

export async function makeFileVersionCurrent(skillHash: string, fileHash: string, version: number): Promise<any> {
  const response = await fetch(`${API_BASE}/skills/${skillHash}/files/${fileHash}/versions/${version}/make-current/`, {
    method: 'POST',
  })

  if (!response.ok)
    throw new Error(`Failed to make file version current: ${response.statusText}`)

  // Invalidate files cache
  invalidateCache(`skills/${skillHash}/files`)
  invalidateCache(`skills/${skillHash}/files/${fileHash}`)

  return response.json()
}

// Regenerate Skill
export async function regenerateSkill(skillHash: string): Promise<any> {
  const response = await fetch(`${API_BASE}/skills/${skillHash}/regenerate/`, {
    method: 'POST',
  })

  if (!response.ok)
    throw new Error(`Failed to regenerate skill: ${response.statusText}`)

  // Invalidate files and skill cache
  invalidateCache(`skills/${skillHash}/files`)
  invalidateCache(`skills/${skillHash}`)

  return response.json()
}

// Toggle Skill Status
export async function toggleSkillStatus(skillHash: string): Promise<any> {
  const response = await fetch(`${API_BASE}/skills/${skillHash}/toggle-status/`, {
    method: 'POST',
  })

  if (!response.ok)
    throw new Error(`Failed to toggle skill status: ${response.statusText}`)

  // Invalidate skill cache
  invalidateCache(`skills/${skillHash}`)

  return response.json()
}

// Re-initialize Repository Discovery
export async function reinitializeRepositoryDiscovery(repositoryGid: string): Promise<any> {
  const response = await fetch(`${API_BASE}/skills/repositories/${repositoryGid}/reinitialize/`, {
    method: 'POST',
  })

  if (!response.ok)
    throw new Error(`Failed to reinitialize repository discovery: ${response.statusText}`)

  // Invalidate repository cache
  invalidateCache(`skills/repositories/${repositoryGid}`)

  return response.json()
}

// Generate More Suggestions (without deleting existing ones)
export async function generateMoreSuggestions(repositoryGid: string): Promise<any> {
  const response = await fetch(`${API_BASE}/skills/repositories/${repositoryGid}/generate-more/`, {
    method: 'POST',
  })

  if (!response.ok)
    throw new Error(`Failed to generate more suggestions: ${response.statusText}`)

  // Invalidate repository cache
  invalidateCache(`skills/repositories/${repositoryGid}`)

  return response.json()
}

// Delete Skills Repository
export async function deleteSkillsRepository(repositoryGid: string): Promise<any> {
  const response = await fetch(`${API_BASE}/skills/repositories/${repositoryGid}/`, {
    method: 'DELETE',
  })

  if (!response.ok)
    throw new Error(`Failed to delete skills repository: ${response.statusText}`)

  // Invalidate repository cache
  invalidateCache(`skills/repositories/${repositoryGid}`)

  return response.json()
}

// Create Skill from Suggested Skill
export async function createSkillFromSuggestion(suggestionId: number): Promise<any> {
  const response = await fetch(`${API_BASE}/skills/suggested-skills/${suggestionId}/create/`, {
    method: 'POST',
  })

  if (!response.ok)
    throw new Error(`Failed to create skill from suggestion: ${response.statusText}`)

  return response.json()
}

// Reject Suggested Skill
export async function rejectSuggestedSkill(suggestionId: number): Promise<any> {
  const response = await fetch(`${API_BASE}/skills/suggested-skills/${suggestionId}/reject/`, {
    method: 'POST',
  })

  if (!response.ok)
    throw new Error(`Failed to reject suggested skill: ${response.statusText}`)

  return response.json()
}

// Unreject Suggested Skill (restore to pending)
export async function unrejectSuggestedSkill(suggestionId: number): Promise<any> {
  const response = await fetch(`${API_BASE}/skills/suggested-skills/${suggestionId}/unreject/`, {
    method: 'POST',
  })

  if (!response.ok)
    throw new Error(`Failed to unreject suggested skill: ${response.statusText}`)

  return response.json()
}

// Create Skills Repositories
export interface CreateSkillsRepositoryData {
  repository_gid: string
  source_path_prefix_includes?: string
}

export interface CreateSkillsRepositoriesResult {
  created: Array<{
    gid: string
    repository_owner: string
    repository_name: string
    source_path_prefix_includes?: string
  }>
  errors: string[]
}

export async function createSkillsRepositories(
  repositories: CreateSkillsRepositoryData[],
): Promise<CreateSkillsRepositoriesResult> {
  const response = await fetch(`${API_BASE}/skills/repositories/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ repositories }),
  })

  if (!response.ok)
    throw new Error(`Failed to create skills repositories: ${response.statusText}`)

  // Invalidate repositories cache
  invalidateCache('skills/repositories')

  return response.json()
}
